import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useForm, useFieldArray } from "react-hook-form";
import { useSelector } from "react-redux";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Typography,
  IconButton,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { PageHeader } from "../../component";
import ReusableField from "../../component/fields";
import FileUploader from "../../common/FileUploader";
import { useApi, useDirect } from "../../hooks";
import { apiMenuArticle, method } from "../../apiPath";
import keyNames from "../../keyName";
import { getToast } from "../../common/utils";

const { get, post, put } = method;

const emptyClient = { image: "" };
const emptyService = { title: "", subtitle: "" };
const emptyFaq = { question: "", answer: "" };

const AddMenuArticle = () => {
  const hitApi = useApi();
  const navigate = useNavigate();
  const { state } = useLocation();
  const directDispatch = useDirect();

  const storeData = useSelector((s) => s.mainReducer);
  const { menuArticleData, extra, isLoading } = storeData;

  const [metaImage, setMetaImage] = useState(state?.meta_image || "");
  const [heroImage, setHeroImage] = useState(state?.image || "");
  const [isActive, setIsActive] = useState(
    state?.isActive !== undefined ? state.isActive : true
  );

  const [endPoint, setEndPoint] = useState({
    path: apiMenuArticle,
    method: post,
    type: state === "add" ? "prepend" : "edit",
  });

  const {
    register,
    control,
    handleSubmit,
    formState: { errors: formErrors },
    setValue,
    setError,
    clearErrors,
  } = useForm({
    defaultValues: {
      meta_title: state?.meta_title || "",
      meta_description: state?.meta_description || "",
      heading1: state?.heading1 || "",
      subheading1: state?.subheading1 || "",
      ctaLink: state?.ctaLink || "",
      video: state?.video || "",
      heading2: state?.heading2 || "",
      subheading2: state?.subheading2 || "",
      heading3: state?.heading3 || "",
      subheading3: state?.subheading3 || "",
      client:
        Array.isArray(state?.client) && state.client.length > 0
          ? state.client
          : [emptyClient],
      services1:
        Array.isArray(state?.services1) && state.services1.length > 0
          ? state.services1
          : [emptyService],
      service2:
        Array.isArray(state?.service2) && state.service2.length > 0
          ? state.service2
          : [emptyService],
      FAQ:
        Array.isArray(state?.FAQ) && state.FAQ.length > 0
          ? state.FAQ
          : [emptyFaq],
    },
  });

  const clientArray = useFieldArray({ control, name: "client" });
  const services1Array = useFieldArray({ control, name: "services1" });
  const service2Array = useFieldArray({ control, name: "service2" });
  const faqArray = useFieldArray({ control, name: "FAQ" });

  useEffect(() => {
    if (state && state !== "add") {
      setEndPoint({
        path: `${apiMenuArticle}/${state._id}`,
        method: put,
        type: "edit",
      });
      setMetaImage(state.meta_image || "");
      setHeroImage(state.image || "");
      setIsActive(state.isActive !== undefined ? state.isActive : true);
    }
  }, [state]);

  useEffect(() => {
    if (menuArticleData === null) {
      hitApi(apiMenuArticle, null, get, keyNames.menuArticleData);
    }
  }, [menuArticleData, hitApi]);

  useEffect(() => {
    if (extra?.isNavigate === "menu-articles") {
      navigate("/app/menu-articles");
    }
    return () => directDispatch(null, keyNames.extra);
  }, [extra, directDispatch, navigate]);

  const allowedImageExts = [".png", ".jpg", ".jpeg", ".webp"];
  const isValidImageUrl = (url) => {
    const lower = (url || "").toLowerCase();
    return allowedImageExts.some((ext) => lower.endsWith(ext));
  };

  const onSubmit = (values) => {
    if (metaImage && !isValidImageUrl(metaImage)) {
      setError("meta_image", {
        type: "manual",
        message: "Image format not supported",
      });
      return;
    }
    if (heroImage && !isValidImageUrl(heroImage)) {
      setError("image", {
        type: "manual",
        message: "Image format not supported",
      });
      return;
    }

    const finalData = {
      ...values,
      meta_image: metaImage,
      image: heroImage,
      isActive,
      client: (values.client || []).filter((item) => item?.image?.trim()),
      services1: (values.services1 || []).filter(
        (item) => item?.title?.trim() || item?.subtitle?.trim()
      ),
      service2: (values.service2 || []).filter(
        (item) => item?.title?.trim() || item?.subtitle?.trim()
      ),
      FAQ: (values.FAQ || []).filter(
        (item) => item?.question?.trim() || item?.answer?.trim()
      ),
    };

    hitApi(
      endPoint.path,
      finalData,
      endPoint.method,
      keyNames.menuArticleData,
      getToast("success"),
      { type: endPoint.type, isNavigate: "menu-articles" }
    );
  };

  const btn = {
    back: () => navigate("/app/menu-articles"),
    show: false,
    text: "Save & Publish",
    type: "contained",
  };

  return (
    <>
      <PageHeader
        title={state?._id ? "Edit Menu Article" : "Add New Menu Article"}
        btn={btn}
      />
      <Box
        component="form"
        noValidate
        onSubmit={handleSubmit(onSubmit)}
        sx={{ mt: 3 }}
      >
        <Card sx={{ mb: 4, boxShadow: 3, borderRadius: 2 }}>
          <CardHeader title="Meta Section" />
          <CardContent>
            <Grid container spacing={3}>
              <Grid item size={{ xs: 12, md: 12 }}>
                <ReusableField
                  label="Meta Title"
                  name="meta_title"
                  type="text"
                  fullWidth
                  {...register("meta_title")}
                />
              </Grid>

              <Grid item size={{ xs: 12, md: 6 }}>
                <Card elevation={1} sx={{ p: 2 }}>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                    <FileUploader
                      url={metaImage}
                      urlType="image"
                      type="menu_article_meta_image"
                      onFileUpload={(url) => {
                        setMetaImage(url);
                        clearErrors("meta_image");
                      }}
                      onInvalidFile={(msg) =>
                        setError("meta_image", { type: "manual", message: msg })
                      }
                      showPreview={false}
                      buttonText="Upload Image"
                    />
                    <ReusableField
                      label="Meta Image"
                      name="meta_image"
                      type="text"
                      value={metaImage}
                      onChange={(e) => setMetaImage(e.target.value)}
                      fullWidth
                      error={!!formErrors.meta_image}
                      helperText={
                        formErrors.meta_image?.message || ""                      }
                    />
                  </Box>
                </Card>
              </Grid>

              <Grid item size={{ md: 6, xs: 12 }}>
                <ReusableField
                  label="Meta Description"
                  name="meta_description"
                  type="textarea"
                  rows={4}
                  multiline
                  fullWidth
                  {...register("meta_description")}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card sx={{ mb: 4, boxShadow: 3, borderRadius: 2 }}>
          <CardHeader title="Section 1" />
          <CardContent>
            <Grid container spacing={2}>
              <Grid item size={{ xs: 12, md: 6 }}>
                <ReusableField
                  label="Heading 1"
                  name="heading1"
                  type="text"
                  fullWidth
                  {...register("heading1", {
                    required: "Heading 1 is required",
                  })}
                  error={!!formErrors.heading1}
                  helperText={formErrors.heading1?.message}
                />
              </Grid>
              <Grid item size={{ xs: 12, md: 6 }}>
                <ReusableField
                  label="Subheading 1"
                  name="subheading1"
                  type="text"
                  fullWidth
                  {...register("subheading1")}
                />
              </Grid>
              <Grid item size={{ xs: 12, md: 6 }}>
                <ReusableField
                  label="CTA Link"
                  name="ctaLink"
                  type="text"
                  fullWidth
                  {...register("ctaLink")}
                />
              </Grid>
              <Grid item size={{ xs: 12, md: 6 }}>
                <ReusableField
                  label="Video URL"
                  name="video"
                  type="url"
                  fullWidth
                  {...register("video")}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card sx={{ mb: 4, boxShadow: 3, borderRadius: 2 }}>
          <CardHeader
            title="Client Logos"
            action={
              <Button
                startIcon={<AddCircleOutlineIcon />}
                onClick={() => clientArray.append(emptyClient)}
              >
                Add Client
              </Button>
            }
          />
          <CardContent>
            <Grid container spacing={2}>
              {clientArray.fields.map((item, index) => (
                <Grid key={item.id} item size={{ xs: 12, md: 6 }}>
                  <Card elevation={1} sx={{ p: 2 }}>
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                    >
                      <FileUploader
                        url={item?.image || ""}
                        type={`menu_article_client_${index + 1}`}
                        urlType="image"
                        onFileUpload={(url) => {
                          setValue(`client.${index}.image`, url, {
                            shouldValidate: true,
                            shouldDirty: true,
                          });
                          clearErrors(`client.${index}.image`);
                        }}
                        onInvalidFile={(msg) =>
                          setError(`client.${index}.image`, {
                            type: "manual",
                            message: msg || "Image format not supported",
                          })
                        }
                        showPreview={false}
                        buttonText={`Upload Client ${index + 1} Logo`}
                      />
                      <Box
                        sx={{ display: "flex", gap: 1, alignItems: "center" }}
                      >
                        <ReusableField
                          label={`Client Image ${index + 1}`}
                          name={`client.${index}.image`}
                          type="text"
                          fullWidth
                          error={!!formErrors?.client?.[index]?.image}
                          helperText={formErrors?.client?.[index]?.image?.message}
                          {...register(`client.${index}.image`)}
                        />
                        <IconButton
                          color="error"
                          onClick={() => clientArray.remove(index)}
                          disabled={clientArray.fields.length === 1}
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>

        <Card sx={{ mb: 4, boxShadow: 3, borderRadius: 2 }}>
          <CardHeader title="Section 2" />
          <CardContent>
            <Grid container spacing={2}>
              <Grid item size={{ xs: 12, md: 6 }}>
                <ReusableField
                  label="Heading 2"
                  name="heading2"
                  type="text"
                  fullWidth
                  {...register("heading2")}
                />
              </Grid>
              <Grid item size={{ xs: 12, md: 6 }}>
                <ReusableField
                  label="Subheading 2"
                  name="subheading2"
                  type="text"
                  fullWidth
                  {...register("subheading2")}
                />
              </Grid>

              <Grid item size={{ xs: 12 }}>
                <Card elevation={1} sx={{ p: 2 }}>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                    <Typography>Section Image</Typography>
                    <FileUploader
                      url={heroImage}
                      type="menu_article_image"
                      urlType="image"
                      onFileUpload={(url) => {
                        setHeroImage(url);
                        clearErrors("image");
                      }}
                      onInvalidFile={(msg) =>
                        setError("image", { type: "manual", message: msg })
                      }
                      showPreview={false}
                      buttonText="Upload Image"
                    />
                    <ReusableField
                      label="Image URL"
                      name="image"
                      type="text"
                      value={heroImage}
                      onChange={(e) => setHeroImage(e.target.value)}
                      fullWidth
                      error={!!formErrors.image}
                      helperText={
                        formErrors.image?.message || ""                      }
                    />
                  </Box>
                </Card>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card sx={{ mb: 4, boxShadow: 3, borderRadius: 2 }}>
          <CardHeader
            title="Services 1"
            action={
              <Button
                startIcon={<AddCircleOutlineIcon />}
                onClick={() => services1Array.append(emptyService)}
              >
                Add Service
              </Button>
            }
          />
          <CardContent>
            <Grid container spacing={2}>
              {services1Array.fields.map((item, index) => (
                <Grid key={item.id} item size={{ xs: 12 }}>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <Grid container spacing={2} sx={{ flex: 1 }}>
                      <Grid item size={{ xs: 12, md: 4 }}>
                        <ReusableField
                          label={`Service ${index + 1} Title`}
                          name={`services1.${index}.title`}
                          type="text"
                          fullWidth
                          {...register(`services1.${index}.title`)}
                        />
                      </Grid>
                      <Grid item size={{ xs: 12, md: 8 }}>
                        <ReusableField
                          label={`Service ${index + 1} Subtitle`}
                          name={`services1.${index}.subtitle`}
                          type="text"
                          fullWidth
                          {...register(`services1.${index}.subtitle`)}
                        />
                      </Grid>
                    </Grid>
                    <IconButton
                      color="error"
                      onClick={() => services1Array.remove(index)}
                      disabled={services1Array.fields.length === 1}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>

        <Card sx={{ mb: 4, boxShadow: 3, borderRadius: 2 }}>
          <CardHeader title="Section 3" />
          <CardContent>
            <Grid container spacing={2}>
              <Grid item size={{ xs: 12, md: 6 }}>
                <ReusableField
                  label="Heading 3"
                  name="heading3"
                  type="text"
                  fullWidth
                  {...register("heading3")}
                />
              </Grid>
              <Grid item size={{ xs: 12, md: 6 }}>
                <ReusableField
                  label="Subheading 3"
                  name="subheading3"
                  type="text"
                  fullWidth
                  {...register("subheading3")}
                />
              </Grid>
              <Grid item size={{ xs: 12, md: 4 }}>
                <Card elevation={2} sx={{ px: 3, py: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                    }}
                  >
                    <ReusableField
                      type="switch"
                      label="Is Active"
                      name="isActive"
                      value={isActive}
                      onChange={(e) => setIsActive(e.target.checked)}
                    />
                  </Box>
                </Card>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card sx={{ mb: 4, boxShadow: 3, borderRadius: 2 }}>
          <CardHeader
            title="Service 2"
            action={
              <Button
                startIcon={<AddCircleOutlineIcon />}
                onClick={() => service2Array.append(emptyService)}
              >
                Add Item
              </Button>
            }
          />
          <CardContent>
            <Grid container spacing={2}>
              {service2Array.fields.map((item, index) => (
                <Grid key={item.id} item size={{ xs: 12 }}>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <Grid container spacing={2} sx={{ flex: 1 }}>
                      <Grid item size={{ xs: 12, md: 4 }}>
                        <ReusableField
                          label={`Item ${index + 1} Title`}
                          name={`service2.${index}.title`}
                          type="text"
                          fullWidth
                          {...register(`service2.${index}.title`)}
                        />
                      </Grid>
                      <Grid item size={{ xs: 12, md: 8 }}>
                        <ReusableField
                          label={`Item ${index + 1} Subtitle`}
                          name={`service2.${index}.subtitle`}
                          type="text"
                          fullWidth
                          {...register(`service2.${index}.subtitle`)}
                        />
                      </Grid>
                    </Grid>
                    <IconButton
                      color="error"
                      onClick={() => service2Array.remove(index)}
                      disabled={service2Array.fields.length === 1}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>

        <Card sx={{ mb: 4, boxShadow: 3, borderRadius: 2 }}>
          <CardHeader
            title="FAQ"
            action={
              <Button
                startIcon={<AddCircleOutlineIcon />}
                onClick={() => faqArray.append(emptyFaq)}
              >
                Add FAQ
              </Button>
            }
          />
          <CardContent>
            <Grid container spacing={2}>
              {faqArray.fields.map((item, index) => (
                <Grid key={item.id} item size={{ xs: 12 }}>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <Grid container spacing={2} sx={{ flex: 1 }}>
                      <Grid item size={{ xs: 12, md: 4 }}>
                        <ReusableField
                          label={`Question ${index + 1}`}
                          name={`FAQ.${index}.question`}
                          type="text"
                          fullWidth
                          {...register(`FAQ.${index}.question`)}
                        />
                      </Grid>
                      <Grid item size={{ xs: 12, md: 8 }}>
                        <ReusableField
                          label={`Answer ${index + 1}`}
                          name={`FAQ.${index}.answer`}
                          type="textarea"
                          rows={2}
                          fullWidth
                          {...register(`FAQ.${index}.answer`)}
                        />
                      </Grid>
                    </Grid>
                    <IconButton
                      color="error"
                      onClick={() => faqArray.remove(index)}
                      disabled={faqArray.fields.length === 1}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>

        <Box sx={{ textAlign: "center", mt: 4 }}>
          <Button
            size="large"
            type="submit"
            variant="contained"
            sx={{ px: 5, py: 1.5, fontSize: "1rem", borderRadius: "8px" }}
            loading={isLoading === "menuArticleData"}
            loadingPosition="start"
          >
            Save & Publish
          </Button>
        </Box>
      </Box>
    </>
  );
};

export default AddMenuArticle;



